import { useState, useEffect, useMemo, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { ArrowLeft, RefreshCw, Settings, User, List, Cpu, FileText, AlertTriangle, Clock, Wrench, Camera, ChevronLeft, ChevronRight, MessageSquare, CheckCircle, Image, Mic, Loader2, Lightbulb, Lock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { toast } from "sonner";
import { StepsOverviewDialog } from "@/components/StepsOverviewDialog";
import { AIAssistantDialog } from "@/components/AIAssistantDialog";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { DocumentStepDialog } from "@/components/DocumentStepDialog";
import { QualityGradingContent } from "@/components/QualityGradingContent";
import { ErrorCodeDiagnostic } from "@/components/ErrorCodeDiagnostic";
import { useWorkflowDefaults, useErrorCodes } from "@/hooks/useWorkflowDefaults";
import { usePhaseAccess } from "@/hooks/usePhaseRoles";
import { usePhaseDefaults } from "@/hooks/usePhaseDefaults";
import { supabase } from "@/integrations/supabase/client";
import { useWorkOrderProgress } from "@/hooks/useWorkOrderProgress";
import { MatchingManualsPanel } from "@/components/MatchingManualsPanel";
import { ExplodedViewsPanel } from "@/components/workflow/ExplodedViewsPanel";
import { seriesLabelFor } from "@/hooks/useMatchingManuals";
import { usePhaseTimer } from "@/hooks/usePhaseTimer";
import { useAppliance } from "@/hooks/useAppliance";
import { WorkflowFinishActions } from "@/components/WorkflowFinishActions";
import { usePassportForWorkOrder } from "@/hooks/useProductPassport";

interface Step {
  id: number;
  phase: number;
  phaseName: string;
  phaseCategory?: string;
  title: string;
  description: string;
  isSafetyCritical: boolean;
  isDocumentation?: boolean;
  isGrading?: boolean;
  isDiagnostic?: boolean;
  safetyWarning?: string;
  estimatedTime: string;
  tools: string[];
  completed: boolean;
  workflowItemId?: string;
}

const formatEstTime = (item: { estimated_minutes?: number | null }, _fallback: string): string => {
  // Missing values in workflow management count as 0 — do not apply default fallbacks.
  const m = item?.estimated_minutes;
  if (m != null && Number.isFinite(Number(m))) {
    const num = Number(m);
    if (num <= 0) return ""; // 0 means "do not display time"
    return `${num} min`;
  }
  return "";
};

// Phase mapping from workflow categories
const PHASE_CONFIG = [
  { key: "refurbishStart" as const, label: "Refurbish Start", isSafetyCritical: true },
  { key: "errorCodes" as const, label: "Common Error Codes", isSafetyCritical: false },
  { key: "troubleshooting" as const, label: "Pre-repair Troubleshooting", isSafetyCritical: false },
  { key: "partsReplacement" as const, label: "Parts Replacement Guides", isSafetyCritical: false },
  { key: "verification" as const, label: "Final Verification", isSafetyCritical: false },
  { key: "cleaning" as const, label: "Cleaning", isSafetyCritical: false },
];

// Map of phase labels (English source) to translation keys under refurb.process.phaseNames
const PHASE_NAME_KEYS: Record<string, string> = {
  "Refurbish Start": "refurbishStart",
  "Common Error Codes": "errorCodes",
  "Pre-repair Troubleshooting": "troubleshooting",
  "Parts Replacement Guides": "partsReplacement",
  "Final Verification": "verification",
  "Cleaning": "cleaning",
  "Quality & Completion": "qualityCompletion",
  "Initial Assessment": "initialAssessment",
};

// Final hardcoded steps that always appear at the end
const FINAL_STEPS: Omit<Step, "id" | "phase" | "phaseName">[] = [
  {
    title: "Final Preparation",
    description: "Final exterior cleaning, level adjustment, documentation, and quality labeling.",
    isSafetyCritical: false, estimatedTime: "15 min",
    tools: ["Level", "Documentation Forms", "Labels"], completed: false,
  },
  {
    title: "Visual Inspection & Grading",
    description: "Comprehensive visual assessment to determine quality grade (A, B, or C) based on aesthetic condition, functionality, and marketability.",
    isSafetyCritical: false, isDocumentation: true, isGrading: true,
    estimatedTime: "10 min", tools: ["Inspection Checklist", "Grading Forms"], completed: false,
  },
];

const RefurbishmentProcess = () => {
  const { t } = useTranslation("refurb");
  const localizePhaseName = (name: string): string => {
    const key = PHASE_NAME_KEYS[name];
    return key ? t(`process.phaseNames.${key}`, { defaultValue: name }) : name;
  };
  const localizeStepTitle = (title: string): string => {
    if (title === "Error Code Diagnostic") return t("process.errorCodeDiagnostic");
    if (title === "Final Preparation") return t("process.finalPreparation");
    if (title === "Visual Inspection & Grading") return t("process.visualInspection");
    return title;
  };
  const localizeStepDescription = (title: string, fallback: string): string => {
    if (title === "Error Code Diagnostic") return t("process.errorCodeDiagnosticDesc");
    if (title === "Final Preparation") return t("process.finalPreparationDesc");
    if (title === "Visual Inspection & Grading") return t("process.visualInspectionDesc");
    return fallback;
  };
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const routeParams = new URLSearchParams(location.search);
  const requestedWorkOrderId = routeParams.get("workOrderId");
  const openCompletedOrder = routeParams.get("completed") === "true";
  const [manualUrl, setManualUrl] = useState<string | null>(
    (location.state as any)?.manualUrl || null
  );
  const [manualId, setManualId] = useState<string | null>(
    (location.state as any)?.manualId || null
  );
  const [manualTitle, setManualTitle] = useState<string | null>(
    (location.state as any)?.manualTitle || null
  );
  const [manualIsFallback, setManualIsFallback] = useState(false);
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [completedSteps, setCompletedSteps] = useState<number[]>([]);
  const [stepDocumentation, setStepDocumentation] = useState<Record<number, {
    photos: number; voiceNotes: number; textNotes: number; timestamp: string;
  }>>({});
  const [showStepsOverview, setShowStepsOverview] = useState(false);
  const [showAIAssistant, setShowAIAssistant] = useState(false);
  const [showDocumentDialog, setShowDocumentDialog] = useState(false);
  const [showTips, setShowTips] = useState(false);
  const [selectedErrorCodeIds, setSelectedErrorCodeIds] = useState<string[]>([]);
  const [dynamicTroubleshooting, setDynamicTroubleshooting] = useState<{ id: string; title: string; description: string | null }[]>([]);
  const [dynamicParts, setDynamicParts] = useState<{ id: string; title: string; description: string | null }[]>([]);
  const [diagnosticConfirmed, setDiagnosticConfirmed] = useState(false);
  const [progressLoaded, setProgressLoaded] = useState(false);

  const { appliance: loadedAppliance, isLoading: isLoadingAppliance, notFound } = useAppliance(id);

  useEffect(() => {
    if (notFound) {
      toast.error(t("common.applianceNotFound"));
      navigate("/work-orders");
    }
  }, [notFound, navigate, t]);

  const appliance = loadedAppliance ?? {
    name: t("common.loading"),
    modelNumber: "",
    category: "",
    brand: "",
  };

  // Work order progress persistence
  const { workOrderId, savedProgress, isLoading: isLoadingProgress, saveProgress } = useWorkOrderProgress(id, "refurbish", requestedWorkOrderId);

  // Existing passport for this work order — if present, the workflow is finalized.
  const { passport: existingPassport } = usePassportForWorkOrder(workOrderId);
  const isWorkflowFinalized = !!existingPassport;

  // Captured quality grade (set by QualityGradingContent)
  const [qualityGrade, setQualityGrade] = useState<string | null>(null);

  // Fetch workflow defaults for refurbishment
  const { defaults, isLoading: isLoadingWorkflow } = useWorkflowDefaults(
    appliance.brand, appliance.category, "refurbish", appliance.modelNumber
  );

  // Fetch ALL error codes for the brand (not filtered by workflow defaults)
  const { errorCodes: allBrandErrorCodes } = useErrorCodes(appliance.brand, appliance.category);

  const { canAccessPhase, isLoading: isLoadingAccess } = usePhaseAccess(appliance.category);
  const { isPhaseEnabled, getPhaseMinutes } = usePhaseDefaults(appliance.category, appliance.brand);



  // Build phases and steps from workflow data + dynamic items from error code selection
  const { steps, phaseNames, phaseCategories } = useMemo(() => {
    const allSteps: Step[] = [];
    const names: string[] = [];
    const categories: string[] = [];
    let stepId = 1;
    let phaseNum = 1;

    const categoryMap: Record<string, string> = {
      refurbishStart: "refurbish_start",
      errorCodes: "error_codes",
      partsReplacement: "parts_replacement",
      troubleshooting: "troubleshooting",
      verification: "verification",
      cleaning: "cleaning",
    };

    for (const phaseConfig of PHASE_CONFIG) {
      // Skip phase entirely if admin disabled it for refurbish
      if (!isPhaseEnabled(categoryMap[phaseConfig.key] || phaseConfig.key, "refurbish")) continue;
      if (phaseConfig.key === "errorCodes") {
        // Always add Error Code Diagnostic as a single mandatory step
        names.push(phaseConfig.label);
        categories.push("error_codes");
        allSteps.push({
          id: stepId++,
          phase: phaseNum,
          phaseName: phaseConfig.label,
          phaseCategory: "error_codes",
          title: "Error Code Diagnostic",
          description: "Identify error codes displayed by the appliance. Select all applicable codes to update the workflow with relevant troubleshooting and parts replacement steps.",
          isSafetyCritical: false,
          isDiagnostic: true,
          estimatedTime: formatEstTime({ estimated_minutes: getPhaseMinutes("error_codes") }, "5 min"),
          tools: [],
          completed: false,
        });
        phaseNum++;
        continue;
      }

      if (phaseConfig.key === "troubleshooting") {
        // Combine default troubleshooting + dynamic from error codes
        const baseItems = defaults.troubleshooting || [];
        const dynamicIds = new Set(dynamicTroubleshooting.map((d) => d.id));
        // Add base items
        const combinedItems = [
          ...baseItems,
          // Add dynamic items not already in base
          ...dynamicTroubleshooting.filter((d) => !baseItems.some((b) => b.id === d.id)),
        ];

        // Always add the phase even if empty
        names.push(phaseConfig.label);
        categories.push("troubleshooting");
        for (const item of combinedItems) {
          const isDynamic = dynamicIds.has(item.id) && !baseItems.some((b) => b.id === item.id);
          allSteps.push({
            id: stepId++,
            phase: phaseNum,
            phaseName: phaseConfig.label,
            phaseCategory: "troubleshooting",
            title: item.title,
            description: item.description || "",
            isSafetyCritical: false,
            isDocumentation: false,
            estimatedTime: formatEstTime(item as any, "10 min"),
            tools: [],
            completed: false,
            workflowItemId: item.id,
          });
        }
        phaseNum++;
        continue;
      }

      if (phaseConfig.key === "partsReplacement") {
        const baseItems = defaults.partsReplacement || [];
        const dynamicIds = new Set(dynamicParts.map((d) => d.id));
        const combinedItems = [
          ...baseItems,
          ...dynamicParts.filter((d) => !baseItems.some((b) => b.id === d.id)),
        ];
        // Always add the phase even if empty
        names.push(phaseConfig.label);
        categories.push("parts_replacement");
        for (const item of combinedItems) {
          allSteps.push({
            id: stepId++,
            phase: phaseNum,
            phaseName: phaseConfig.label,
            phaseCategory: "parts_replacement",
            title: item.title,
            description: item.description || "",
            isSafetyCritical: false,
            isDocumentation: false,
            estimatedTime: formatEstTime(item as any, "10 min"),
            tools: [],
            completed: false,
            workflowItemId: item.id,
          });
        }
        phaseNum++;
        continue;
      }

      // Other phases: refurbishStart, verification — always add phase
      const items = defaults[phaseConfig.key] || [];
      names.push(phaseConfig.label);
      categories.push(categoryMap[phaseConfig.key] || phaseConfig.key);
      for (const item of items) {
        allSteps.push({
          id: stepId++,
          phase: phaseNum,
          phaseName: phaseConfig.label,
          phaseCategory: categoryMap[phaseConfig.key] || phaseConfig.key,
          title: item.title,
          description: item.description || "",
          isSafetyCritical: phaseConfig.isSafetyCritical,
          isDocumentation: phaseConfig.key === "verification",
          estimatedTime: formatEstTime(item as any, "10 min"),
          tools: [],
          completed: false,
        });
      }
      phaseNum++;
    }

    // Always add a final "Quality & Completion" phase
    const finalPhaseName = "Quality & Completion";
    names.push(finalPhaseName);
    categories.push("quality_completion");
    for (const fs of FINAL_STEPS) {
      allSteps.push({
        ...fs,
        id: stepId++,
        phase: phaseNum,
        phaseName: finalPhaseName,
        phaseCategory: "quality_completion",
      });
    }

    // If no workflow data yet, use fallback
    if (allSteps.length === FINAL_STEPS.length) {
      const fallbackSteps: Step[] = [
        { id: 1, phase: 1, phaseName: "Initial Assessment", title: "Disconnect Power & Water", description: "Safely disconnect power and water connections.", isSafetyCritical: true, safetyWarning: "Critical – Risk of electrocution", estimatedTime: "10 min", tools: ["Pump Pliers"], completed: false },
        { id: 2, phase: 1, phaseName: "Initial Assessment", title: "Initial Inspection", description: "Perform comprehensive visual inspection.", isSafetyCritical: false, estimatedTime: "15 min", tools: ["Flashlight"], completed: false },
        ...allSteps.map((s, i) => ({ ...s, id: i + 3 })),
      ];
      return { steps: fallbackSteps, phaseNames: ["Initial Assessment", finalPhaseName], phaseCategories: ["initial_assessment", "quality_completion"] };
    }

    return { steps: allSteps, phaseNames: names, phaseCategories: categories };
  }, [defaults, dynamicTroubleshooting, dynamicParts, isPhaseEnabled, getPhaseMinutes]);

  // Restore saved progress from work order
  useEffect(() => {
    if (openCompletedOrder && isLoadingProgress) return;

    if (steps.length > 0 && !progressLoaded) {
      if (openCompletedOrder) {
        // Viewing a completed work order: jump to last step and mark all complete
        setCurrentStepIndex(steps.length - 1);
        setCompletedSteps(steps.map((s) => s.id));
        if (savedProgress?.selectedErrorCodeIds && savedProgress.selectedErrorCodeIds.length > 0) {
          setSelectedErrorCodeIds(savedProgress.selectedErrorCodeIds);
          setDiagnosticConfirmed(true);
        }
        setProgressLoaded(true);
      } else if (savedProgress) {
        setCurrentStepIndex(Math.min(savedProgress.currentStepIndex, steps.length - 1));
        setCompletedSteps(savedProgress.completedSteps || []);
        if (savedProgress.selectedErrorCodeIds && savedProgress.selectedErrorCodeIds.length > 0) {
          setSelectedErrorCodeIds(savedProgress.selectedErrorCodeIds);
          setDiagnosticConfirmed(true);
        }
        setProgressLoaded(true);
      }
    }
  }, [savedProgress, steps, progressLoaded, openCompletedOrder, isLoadingProgress]);

  // Save progress to work order whenever step/completion changes
  useEffect(() => {
    // Completed work orders are opened for review only; never overwrite their saved completion state.
    if (openCompletedOrder) return;

    // Wait for steps to be loaded
    if (steps.length === 0) return;
    
    // If there's saved progress, wait for restore effect to run first
    if (savedProgress && !progressLoaded) return;
    
    // Mark as loaded if not yet (handles fresh start with no saved progress)
    if (!progressLoaded) {
      setProgressLoaded(true);
      // Don't return - save the initial state right away
    }

    const cs = steps[currentStepIndex];
    saveProgress({
      currentStepIndex,
      completedSteps,
      currentPhaseName: cs?.phaseName || "",
      currentPhaseCategory: cs?.phaseCategory || "",
      currentStepTitle: cs?.title || "",
      totalSteps: steps.length,
      completedCount: completedSteps.length,
      selectedErrorCodeIds,
    });
  }, [currentStepIndex, completedSteps, steps, savedProgress, progressLoaded, saveProgress, selectedErrorCodeIds, openCompletedOrder]);


  const handleDiagnosticConfirm = useCallback(async (selectedIds: string[]) => {
    setSelectedErrorCodeIds(selectedIds);
    setDiagnosticConfirmed(true);

    if (selectedIds.length === 0) {
      setDynamicTroubleshooting([]);
      setDynamicParts([]);
      const diagStep = steps.find((s) => s.isDiagnostic);
      if (diagStep && !completedSteps.includes(diagStep.id)) {
        setCompletedSteps((prev) => [...prev, diagStep.id]);
      }
      toast.success(t("process.noErrorCodesContinue"));
      return;
    }

    try {
      const { data: mappings } = await supabase
        .from("workflow_item_mappings")
        .select("target_item_id")
        .in("source_item_id", selectedIds);

      if (mappings && mappings.length > 0) {
        const targetIds = [...new Set(mappings.map((m) => m.target_item_id))];
        const { data: targetItems } = await supabase
          .from("workflow_items")
          .select("id, category, title, description")
          .in("id", targetIds);

        if (targetItems) {
          setDynamicTroubleshooting(targetItems.filter((i) => i.category === "troubleshooting"));
          setDynamicParts(targetItems.filter((i) => i.category === "parts_replacement"));
        }
      } else {
        setDynamicTroubleshooting([]);
        setDynamicParts([]);
      }
    } catch (err) {
      console.error("Error fetching mappings:", err);
    }

    const diagStep = steps.find((s) => s.isDiagnostic);
    if (diagStep && !completedSteps.includes(diagStep.id)) {
      setCompletedSteps((prev) => [...prev, diagStep.id]);
    }
    toast.success(t("process.errorCodesUpdated", { count: selectedIds.length }));
  }, [steps, completedSteps]);


  const totalPhotos = Object.values(stepDocumentation).reduce((sum, doc) => sum + doc.photos, 0);
  const totalVoiceNotes = Object.values(stepDocumentation).reduce((sum, doc) => sum + doc.voiceNotes, 0);
  const totalWrittenNotes = Object.values(stepDocumentation).reduce((sum, doc) => sum + (doc.textNotes > 0 ? 1 : 0), 0);

  const currentStep = steps[currentStepIndex];
  const totalSteps = steps.length;
  const completedStepCount = steps.filter((s) => completedSteps.includes(s.id)).length;
  const progressPercent = totalSteps > 0 ? Math.round((completedStepCount / totalSteps) * 100) : 0;

  // Check if current step's phase is accessible
  const isCurrentPhaseLocked = currentStep ? !canAccessPhase(currentStep.phaseCategory || "") : false;

  // Anonymous phase time tracking — only for the 6 tracked categories
  const TRACKED_PHASES: Record<string, string> = {
    refurbish_start: "Refurbish Start",
    error_codes: "Common Error Codes",
    troubleshooting: "Pre-repair Troubleshooting",
    parts_replacement: "Parts Replacement Guides",
    verification: "Final Verification",
    cleaning: "Cleaning",
  };
  const currentCat = currentStep?.phaseCategory || "";
  const trackedLabel = TRACKED_PHASES[currentCat];
  usePhaseTimer({
    phaseCategory: trackedLabel ? currentCat : null,
    phaseLabel: trackedLabel,
    applianceId: id,
    applianceType: appliance.category,
    brand: appliance.brand,
    enabled: !isCurrentPhaseLocked,
  });

  const getPhaseProgress = (phaseNum: number) => {
    const phaseSteps = steps.filter((s) => s.phase === phaseNum);
    const completed = phaseSteps.filter((s) => completedSteps.includes(s.id)).length;
    return { completed, total: phaseSteps.length };
  };

  const handleCompleteStep = () => {
    if (currentStep.isDocumentation) {
      setShowDocumentDialog(true);
    } else {
      // Direct completion without documentation dialog
      if (!completedSteps.includes(currentStep.id)) {
        setCompletedSteps([...completedSteps, currentStep.id]);
        toast.success(t("process.stepCompletedToast", { id: currentStep.id }));
      }
    }
  };

  const handleDocumentSave = (data: { notes: string; photos: File[]; voiceNotes?: number }) => {
    if (!completedSteps.includes(currentStep.id)) {
      setCompletedSteps([...completedSteps, currentStep.id]);
      setStepDocumentation({
        ...stepDocumentation,
        [currentStep.id]: {
          photos: data.photos.length,
          voiceNotes: data.voiceNotes || 0,
          textNotes: data.notes.length,
          timestamp: `${new Date().toLocaleDateString('en-GB')}, ${new Date().toLocaleTimeString('en-GB')}`,
        },
      });
      toast.success(t("process.stepDocumentedToast", { id: currentStep.id }));
    }
    setShowDocumentDialog(false);
  };

  const handleDocumentSkip = () => {
    if (!completedSteps.includes(currentStep.id)) {
      setCompletedSteps([...completedSteps, currentStep.id]);
      setStepDocumentation({
        ...stepDocumentation,
        [currentStep.id]: {
          photos: 0,
          voiceNotes: 0,
          textNotes: 0,
          timestamp: `${new Date().toLocaleDateString('en-GB')}, ${new Date().toLocaleTimeString('en-GB')}`,
        },
      });
      toast.success(t("process.stepCompletedToast", { id: currentStep.id }));
    }
    setShowDocumentDialog(false);
  };

  const handlePreviousStep = () => {
    if (currentStepIndex > 0) {
      setCurrentStepIndex(currentStepIndex - 1);
    }
  };

  const handleNextStep = () => {
    if (currentStepIndex < totalSteps - 1) {
      setCurrentStepIndex(currentStepIndex + 1);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="flex items-center justify-between px-6 py-4 bg-card border-b border-border">
        <div className="flex items-center gap-6">
          <button
            onClick={() => navigate("/work-orders")}
            className="flex items-center gap-2 text-primary hover:text-primary/80 transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
            <span className="font-medium">{t("common.backToWorkOrders")}</span>
          </button>
          
          <div>
            <h1 className="font-bold text-foreground">FixID</h1>
            <div className="flex items-center gap-1 text-sm text-muted-foreground">
              <RefreshCw className="w-3 h-3" />
              <span>{t("process.subtitleNav")}</span>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-6">
          <div className="text-right">
            <p className="font-medium text-foreground">{appliance.name}</p>
            <div className="flex items-center gap-2 justify-end">
              <span className="text-sm text-muted-foreground">{appliance.modelNumber}</span>
              <span className="category-badge category-badge-washing text-xs">{appliance.category}</span>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            <button className="p-2 rounded-lg hover:bg-muted transition-colors">
              <Settings className="w-5 h-5 text-muted-foreground" />
            </button>
            <button className="p-2 rounded-lg hover:bg-muted transition-colors">
              <User className="w-5 h-5 text-muted-foreground" />
            </button>
          </div>
        </div>
      </header>

      <main className="p-6 max-w-6xl mx-auto">
        {/* Title Card */}
        <div className="bg-card rounded-2xl border border-border p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-refurbish/10 flex items-center justify-center">
                <RefreshCw className="w-6 h-6 text-refurbish" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-foreground">{t("process.title")}</h2>
                <p className="text-muted-foreground">{t("process.stepOf", { current: currentStepIndex + 1, total: totalSteps })}</p>
              </div>
            </div>
            
            <div className="flex items-center gap-4">
              <div className="text-right">
                <p className="text-sm text-muted-foreground">{t("process.overallProgress")}</p>
                <p className="font-semibold text-foreground">{t("process.percentComplete", { pct: progressPercent })}</p>
              </div>
              <Button variant="outline" className="gap-2" onClick={() => setShowStepsOverview(true)}>
                <List className="w-4 h-4" />
                {t("process.viewAllSteps")}
              </Button>
              <Button variant="outline" className="gap-2" onClick={() => setShowAIAssistant(true)}>
                <Cpu className="w-4 h-4" />
                {t("process.aiAssistant")}
                <span className="px-2 py-0.5 bg-muted text-muted-foreground text-xs rounded-full">{t("process.available")}</span>
              </Button>
              {defaults.tips.length > 0 && (
                <Button variant="outline" className="gap-2" onClick={() => setShowTips(true)}>
                  <Lightbulb className="w-4 h-4" />
                  {t("process.tips")}
                  <span className="px-2 py-0.5 bg-muted text-muted-foreground text-xs rounded-full">{defaults.tips.length}</span>
                </Button>
              )}
            </div>
          </div>
          <Progress value={progressPercent} className="h-2" />
        </div>

        {/* Main Content */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Step Content */}
          <div className="lg:col-span-2 space-y-6">
            <MatchingManualsPanel
              brand={appliance.brand}
              modelNumber={appliance.modelNumber}
              activeManualId={manualId}
              onSelectForAI={(mid, title, kind) => {
                setManualId(mid);
                setManualTitle(title);
                setManualUrl(null);
                setManualIsFallback(kind === "fallback");
                toast.success(t("process.manualAttached", { title }));
              }}
            />
            <ExplodedViewsPanel
              brand={appliance.brand}
              applianceType={appliance.category}
              modelNumber={appliance.modelNumber}
              currentWorkflowItemId={currentStep?.workflowItemId ?? null}
              strictModelMatch
            />
            <div className="bg-card rounded-2xl border border-border p-6 relative">
              {/* Phase Lock Overlay */}
              {isCurrentPhaseLocked && (
                <div className="absolute inset-0 bg-background/80 backdrop-blur-sm rounded-2xl z-10 flex flex-col items-center justify-center gap-3">
                  <div className="w-14 h-14 rounded-full bg-muted flex items-center justify-center">
                    <Lock className="w-7 h-7 text-muted-foreground" />
                  </div>
                  <h3 className="font-semibold text-foreground text-lg">{t("process.phaseLocked")}</h3>
                  <p className="text-sm text-muted-foreground text-center max-w-sm">
                    {t("process.phaseLockedDesc", { phase: localizePhaseName(currentStep.phaseName) })}
                  </p>
                </div>
              )}
              {/* Step Header */}
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <span className="px-3 py-1 bg-primary text-primary-foreground text-sm font-medium rounded-full">
                    {t("process.phase", { n: currentStep.phase })}
                  </span>
                  <h3 className="font-semibold text-foreground">{localizeStepTitle(currentStep.title)}</h3>
                  {currentStep.isDocumentation && (
                    <span className="flex items-center gap-1 px-2 py-1 bg-muted text-muted-foreground text-xs font-medium rounded-full border border-border">
                      <Camera className="w-3 h-3" />
                      {t("process.documentation")}
                    </span>
                  )}
                </div>
                {currentStep.isSafetyCritical && (
                  <span className="px-3 py-1 bg-destructive text-destructive-foreground text-sm font-medium rounded-full">
                    {t("process.safetyCritical")}
                  </span>
                )}
              </div>

              {/* Description */}
              <p className="text-muted-foreground mb-4">{localizeStepDescription(currentStep.title, currentStep.description)}</p>

              {/* Safety Warning */}
              {currentStep.safetyWarning && (
                <div className="flex items-start gap-3 p-4 bg-warning/10 border border-warning/20 rounded-xl mb-4">
                  <AlertTriangle className="w-5 h-5 text-warning flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-medium text-foreground">{t("process.safetyWarning")}</p>
                    <p className="text-sm text-muted-foreground">{currentStep.safetyWarning}</p>
                  </div>
                </div>
              )}

              {/* Time & Tools */}
              <div className="flex items-center gap-4 mb-6">
                {currentStep.estimatedTime && (
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Clock className="w-4 h-4" />
                    <span>{t("process.estimated", { time: currentStep.estimatedTime })}</span>
                  </div>
                )}
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Wrench className="w-4 h-4" />
                  <span>{currentStep.tools.join(", ")}</span>
                </div>
              </div>

              {/* Work Documented Info (when completed) */}
              {completedSteps.includes(currentStep.id) && stepDocumentation[currentStep.id] && (
                <div className="bg-refurbish/10 border border-refurbish/20 rounded-xl p-4 mb-4">
                  <div className="flex items-center gap-2 mb-1">
                    <CheckCircle className="w-4 h-4 text-refurbish" />
                    <span className="font-medium text-foreground">{t("process.workDocumented")}</span>
                  </div>
                  <p className="text-sm text-muted-foreground mb-1">
                    {t("process.documentedSummary", {
                      photos: stepDocumentation[currentStep.id].photos,
                      voice: stepDocumentation[currentStep.id].voiceNotes,
                      notes: stepDocumentation[currentStep.id].textNotes > 0 ? t("process.withWrittenNotes") : t("process.noWrittenNotes"),
                    })}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {t("process.loggedOn", { when: stepDocumentation[currentStep.id].timestamp })}
                  </p>
                </div>
              )}

              {/* Diagnostic Screen for Error Codes */}
              {isWorkflowFinalized ? null : currentStep.isDiagnostic && !completedSteps.includes(currentStep.id) ? (
                <ErrorCodeDiagnostic
                  errorCodes={allBrandErrorCodes}
                  onConfirm={handleDiagnosticConfirm}
                  disabled={isCurrentPhaseLocked}
                />
              ) : currentStep.isDiagnostic && completedSteps.includes(currentStep.id) ? (
                <div className="space-y-3">
                  {selectedErrorCodeIds.length > 0 ? (
                    <div className="p-4 bg-primary/5 border border-primary/20 rounded-xl">
                      <p className="text-sm font-medium text-foreground mb-2">
                        {t("process.errorCodesIdentified", { count: selectedErrorCodeIds.length })}
                      </p>
                      <div className="flex flex-wrap gap-1.5">
                        {allBrandErrorCodes
                          .filter((ec) => selectedErrorCodeIds.includes(ec.id))
                          .map((ec) => (
                            <span key={ec.id} className="px-2 py-1 bg-primary/10 text-primary text-xs rounded-md font-medium">
                              {ec.title}
                            </span>
                          ))}
                      </div>
                    </div>
                  ) : (
                    <div className="p-4 bg-muted/50 border border-border rounded-xl">
                      <p className="text-sm text-muted-foreground">{t("process.noErrorCodesIdentified")}</p>
                    </div>
                  )}
                  <Button disabled className="w-full gap-2 bg-refurbish hover:bg-refurbish/90">
                    <CheckCircle className="w-4 h-4" />
                    {t("process.diagnosticComplete")}
                  </Button>
                </div>
              ) : currentStep.isGrading && !completedSteps.includes(currentStep.id) ? (
                <QualityGradingContent
                  onComplete={(grading) => {
                    setCompletedSteps([...completedSteps, currentStep.id]);
                    setQualityGrade(grading.selectedGrade || null);
                    setStepDocumentation({
                      ...stepDocumentation,
                      [currentStep.id]: {
                        photos: grading.photo ? 1 : 0,
                        voiceNotes: 0,
                        textNotes: grading.aestheticScore + grading.functionalityScore + grading.marketabilityScore,
                        timestamp: `${new Date().toLocaleDateString('en-GB')}, ${new Date().toLocaleTimeString('en-GB')}`,
                      },
                    });
                    toast.success(t("process.qualityGradingComplete", { grade: grading.selectedGrade }));
                  }}
                />
              ) : (
                /* Complete Button */
                <Button
                  onClick={handleCompleteStep}
                  disabled={completedSteps.includes(currentStep.id) || isCurrentPhaseLocked}
                  className={`w-full gap-2 ${
                    completedSteps.includes(currentStep.id)
                      ? "bg-refurbish hover:bg-refurbish/90"
                      : "bg-primary hover:bg-primary/90"
                  }`}
                >
                  {completedSteps.includes(currentStep.id) ? (
                    <>
                      <CheckCircle className="w-4 h-4" />
                      {currentStep.isDocumentation ? t("process.completedDocumented") : t("process.completed")}
                    </>
                  ) : currentStep.isDocumentation ? (
                    <>
                      <Camera className="w-4 h-4" />
                      {t("process.completeAndDocument")}
                    </>
                  ) : (
                    <>
                      <CheckCircle className="w-4 h-4" />
                      {t("process.markComplete")}
                    </>
                  )}
                </Button>
              )}
            </div>

            {/* Navigation — hidden once workflow is finalized */}
            {!isWorkflowFinalized && (
              <div className="bg-card rounded-2xl border border-border p-4">
                <div className="flex items-center justify-between">
                  <Button
                    variant="outline"
                    onClick={handlePreviousStep}
                    disabled={currentStepIndex === 0}
                    className="gap-2"
                  >
                    <ChevronLeft className="w-4 h-4" />
                    {t("process.previousStep")}
                  </Button>

                  {!(completedSteps.length === totalSteps && totalSteps > 0) && (
                    <Button
                      onClick={handleNextStep}
                      disabled={currentStepIndex === totalSteps - 1}
                      className="gap-2"
                    >
                      {t("process.nextStep")}
                      <ChevronRight className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              </div>
            )}

            {/* Finish & Passport actions */}
            {id && (
              <WorkflowFinishActions
                applianceId={id}
                workOrderId={workOrderId}
                workflowType="refurbish"
                brand={appliance.brand}
                modelNumber={appliance.modelNumber}
                category={appliance.category}
                applianceName={appliance.name}
                qualityGrade={qualityGrade}
                summaryData={{
                  stepsCompleted: completedSteps.length,
                  totalSteps,
                  photos: totalPhotos,
                  voiceNotes: totalVoiceNotes,
                  writtenNotes: totalWrittenNotes,
                }}
                completedWorkflowItemIds={steps
                  .filter((s) => completedSteps.includes(s.id) && s.workflowItemId)
                  .map((s) => s.workflowItemId as string)}
                allStepsComplete={completedSteps.length === totalSteps && totalSteps > 0}
              />
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Work Documentation Stats */}
            <div className="bg-card rounded-2xl border border-border p-6">
              <div className="flex items-center gap-2 mb-4">
                <Camera className="w-5 h-5 text-muted-foreground" />
                <h3 className="font-semibold text-foreground">{t("process.workDocumentation")}</h3>
              </div>

              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">{t("process.stepsDocumented")}</span>
                  <span className="text-sm font-medium text-foreground">{completedSteps.length}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">{t("process.photosCaptured")}</span>
                  <span className="text-sm font-medium text-foreground">{totalPhotos}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">{t("process.voiceNotes")}</span>
                  <span className="text-sm font-medium text-foreground">{totalVoiceNotes}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">{t("process.writtenNotes")}</span>
                  <span className="text-sm font-medium text-foreground">{totalWrittenNotes}</span>
                </div>
              </div>
            </div>

            {/* AI Assistant */}
            <div className="bg-card rounded-2xl border border-border p-6">
              <div className="flex items-center gap-2 mb-4">
                <Cpu className="w-5 h-5 text-muted-foreground" />
                <h3 className="font-semibold text-foreground">{t("process.aiAssistant")}</h3>
              </div>

              <Button variant="outline" className="w-full gap-2 mb-4" onClick={() => setShowAIAssistant(true)}>
                <MessageSquare className="w-4 h-4" />
                {t("process.askForHelp")}
              </Button>

              <div className="p-4 bg-primary/5 border border-primary/20 rounded-xl">
                <p className="text-sm text-muted-foreground">
                  {t("process.aiInfo")}
                </p>
              </div>
            </div>

            {/* Phase Overview */}
            <div className="bg-card rounded-2xl border border-border p-6">
              <h3 className="font-semibold text-foreground mb-4">{t("process.phaseOverview")}</h3>
              
              <div className="space-y-4">
                {phaseNames.map((name, idx) => {
                  const phaseNum = idx + 1;
                  const progress = getPhaseProgress(phaseNum);
                  const phasePct = progress.total > 0 ? (progress.completed / progress.total) * 100 : 0;
                  const phaseCat = phaseCategories[idx] || "";
                  const isLocked = !canAccessPhase(phaseCat);
                  return (
                    <div key={phaseNum} className={isLocked ? "opacity-50" : ""}>
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-sm text-foreground truncate mr-2 flex items-center gap-1.5">
                          {isLocked && <Lock className="w-3 h-3 text-muted-foreground flex-shrink-0" />}
                          {localizePhaseName(name)}
                        </span>
                        <span className="text-sm text-muted-foreground flex-shrink-0">
                          {progress.completed}/{progress.total}
                        </span>
                      </div>
                      <Progress value={phasePct} className="h-1.5" />
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>

        {/* Steps Overview Dialog */}
        <StepsOverviewDialog
          open={showStepsOverview}
          onOpenChange={setShowStepsOverview}
          steps={steps}
          currentStepIndex={currentStepIndex}
          completedSteps={completedSteps}
          onNavigateToStep={setCurrentStepIndex}
        />

        {/* AI Assistant Dialog */}
        <AIAssistantDialog
          open={showAIAssistant}
          onOpenChange={setShowAIAssistant}
          currentStep={currentStep}
          modelNumber={appliance.modelNumber}
          manualId={manualId}
          manualTitle={manualTitle}
          manualUrl={manualUrl}
          manualIsFallback={manualIsFallback}
          manualSeriesLabel={seriesLabelFor(appliance.modelNumber)}
          applianceBrand={appliance.brand}
          onManualUploaded={(mid, title) => {
            setManualId(mid);
            setManualTitle(title);
            setManualUrl(null);
            setManualIsFallback(false);
          }}
        />

        {/* Document Step Dialog */}
        <DocumentStepDialog
          open={showDocumentDialog}
          onOpenChange={setShowDocumentDialog}
          step={currentStep}
          onSave={handleDocumentSave}
          onSkip={handleDocumentSkip}
        />

        {/* Tips Dialog */}
        <Dialog open={showTips} onOpenChange={setShowTips}>
          <DialogContent className="max-w-lg max-h-[80vh] p-0">
            <DialogHeader className="p-6 pb-4 border-b border-border">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-amber-500/10 flex items-center justify-center">
                  <Lightbulb className="w-5 h-5 text-amber-500" />
                </div>
                <div>
                  <DialogTitle className="text-lg font-bold">{t("process.tipsTitle")}</DialogTitle>
                  <p className="text-sm text-muted-foreground">
                    {t("process.tipsForBrand", { count: defaults.tips.length, brand: appliance.brand, category: appliance.category })}
                  </p>
                </div>
              </div>
            </DialogHeader>
            <ScrollArea className="max-h-[calc(80vh-100px)]">
              <div className="p-6 space-y-3">
                {defaults.tips.map((tip) => (
                  <div key={tip.id} className="p-4 bg-amber-500/5 border border-amber-500/15 rounded-xl">
                    <p className="font-medium text-foreground">{tip.title}</p>
                    {tip.description && (
                      <p className="text-sm text-muted-foreground mt-1">{tip.description}</p>
                    )}
                  </div>
                ))}
              </div>
            </ScrollArea>
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
};

export default RefurbishmentProcess;
